import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "./useAuth";

// BE enforces a hard cap of 4 goals per account (across manual + hubspot).
export const CUSTOMER_GOALS_CAP = 4;

// Allowed progress values — BE stores progress_pct as a discrete enum, not a
// free int. Sending anything else (e.g. 30) returns 400.
export const PROGRESS_STEPS = [0, 25, 50, 75, 100];

// Milliseconds before a still-pending options fetch degrades to free-text mode.
const OPTIONS_TIMEOUT_MS = 2000;

/**
 * Customer goals for an account — GET/POST/PATCH/DELETE against
 * `accounts/{accountId}/customer-goals/`.
 *
 * Backend contract notes baked in here:
 *  - GET returns `{ goals, reached_count, total_count }`, not a bare array.
 *  - `id` / `accountId` are UUIDs (opaque strings, no coercion).
 *  - `progress_pct` is a discrete enum (PROGRESS_STEPS); free ints → 400.
 *  - `reached_at` is read-only and BE-managed (stamped at 100%, cleared below).
 *  - PATCH accepts `progress_pct`, `note`, or `reached` (write-only convenience:
 *    true → 100% + stamp, false → clears, dropping progress to 0).
 *  - `title` is create-only; PATCHing it → 400.
 *  - hubspot-sourced goals can't be deleted (403).
 *
 * Mutations update local state optimistically and reconcile with the server
 * response; failures revert and re-throw so the caller can surface a toast.
 */
export const useCustomerGoals = (accountId) => {
  const { apiClient } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(!!accountId);
  const [error, setError] = useState(null);

  // Options state — text_source and enum_source from the /options/ endpoint.
  const [textSource, setTextSource] = useState(null);
  const [enumSource, setEnumSource] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(!!accountId);
  // timedOut flips true after OPTIONS_TIMEOUT_MS so the composer degrades to
  // free-text without waiting forever for a slow/failed fetch.
  const optionsTimerRef = useRef(null);
  // Once the timer fires and the composer has degraded to free-text, do not
  // let a late-arriving fetch flip the mode back to combobox mid-interaction —
  // the user has been typing free text for seconds; the visual jump is worse
  // than staying degraded. Reset per fetchOptions invocation.
  const didDegradeRef = useRef(false);

  const baseUrl = `accounts/${accountId}/customer-goals/`;

  const fetchGoals = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get(baseUrl);
      // Response is an object with counts — read the `goals` array off it.
      setGoals(data?.goals ?? []);
    } catch (err) {
      console.error("Error fetching customer goals:", err);
      // Cross-team accounts 404 rather than returning an empty list.
      if (err.response?.status === 404) setError("not_found");
      else if (err.response?.status !== 401) setError("load_failed");
    } finally {
      setLoading(false);
    }
  }, [apiClient, accountId, baseUrl]);

  const fetchOptions = useCallback(async () => {
    if (!accountId) return;
    setOptionsLoading(true);
    didDegradeRef.current = false;
    // Start the degrade timer — if the fetch takes > OPTIONS_TIMEOUT_MS,
    // the composer becomes available as free-text rather than staying locked.
    if (optionsTimerRef.current) clearTimeout(optionsTimerRef.current);
    optionsTimerRef.current = setTimeout(() => {
      didDegradeRef.current = true;
      setOptionsLoading(false);
    }, OPTIONS_TIMEOUT_MS);
    try {
      const { data } = await apiClient.get(`${baseUrl}options/`);
      // If the timer already fired, the user is now in free-text mode. Do NOT
      // flip them back into combobox mode after they've been typing for seconds.
      if (!didDegradeRef.current) {
        setTextSource(data?.text_source ?? null);
        setEnumSource(data?.enum_source ?? null);
      }
    } catch (err) {
      console.error("Error fetching goal options:", err);
      // Degrade silently to free-text — the composer remains usable.
      setTextSource(null);
      setEnumSource(null);
    } finally {
      if (optionsTimerRef.current) clearTimeout(optionsTimerRef.current);
      optionsTimerRef.current = null;
      setOptionsLoading(false);
    }
  }, [apiClient, accountId, baseUrl]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  useEffect(() => {
    fetchOptions();
    return () => {
      if (optionsTimerRef.current) clearTimeout(optionsTimerRef.current);
    };
  }, [fetchOptions]);

  // Derive an optimistic patch from the fields being sent, mirroring the BE's
  // save() side effects so the UI updates instantly before reconciliation.
  const optimisticFor = (payload) => {
    const patch = {};
    if ("progress_pct" in payload) {
      patch.progress_pct = payload.progress_pct;
      patch.reached_at =
        payload.progress_pct >= 100 ? new Date().toISOString() : null;
    }
    if ("reached" in payload) {
      patch.progress_pct = payload.reached ? 100 : 0;
      patch.reached_at = payload.reached ? new Date().toISOString() : null;
    }
    if ("note" in payload) {
      patch.note = payload.note;
      patch.note_updated_at = new Date().toISOString();
    }
    return patch;
  };

  const updateGoal = useCallback(
    async (goalId, payload) => {
      const optimistic = optimisticFor(payload);
      let snapshot;
      setGoals((prev) => {
        snapshot = prev;
        return prev.map((g) => (g.id === goalId ? { ...g, ...optimistic } : g));
      });
      try {
        const { data } = await apiClient.patch(`${baseUrl}${goalId}/`, payload);
        // Reconcile with server truth (reached_at, note_updated_at, etc.).
        setGoals((prev) =>
          prev.map((g) => (g.id === goalId ? { ...g, ...data } : g)),
        );
        return data;
      } catch (err) {
        setGoals(snapshot);
        throw err;
      }
    },
    [apiClient, baseUrl],
  );

  const addGoal = useCallback(
    async ({ title, option_value } = {}) => {
      // No optimistic insert — we need the server's id/created_at ordering.
      const body = { title };
      // Only include option_value when truthy so text-goals teams see today's
      // payload byte-for-byte — the BE ignores an absent key.
      if (option_value) body.option_value = option_value;
      const { data } = await apiClient.post(baseUrl, body);
      setGoals((prev) => [...prev, data]);
      return data;
    },
    [apiClient, baseUrl],
  );

  const deleteGoal = useCallback(
    async (goalId) => {
      let snapshot;
      setGoals((prev) => {
        snapshot = prev;
        return prev.filter((g) => g.id !== goalId);
      });
      try {
        await apiClient.delete(`${baseUrl}${goalId}/`);
      } catch (err) {
        setGoals(snapshot);
        throw err;
      }
    },
    [apiClient, baseUrl],
  );

  const reachedCount = goals.filter((g) => g.reached_at != null).length;

  return {
    goals,
    loading,
    error,
    reachedCount,
    totalCount: goals.length,
    atCap: goals.length >= CUSTOMER_GOALS_CAP,
    cap: CUSTOMER_GOALS_CAP,
    refetch: fetchGoals,
    addGoal,
    updateGoal,
    deleteGoal,
    // Options sources — dual-source shape from /options/ endpoint.
    textSource,
    enumSource,
    optionsLoading,
  };
};
